/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import { DataField } from '../../../types';
import { getRowsPerAvailableSpace } from '../../../utils-generic';
import { mapClassifierToTableRowOption } from '../../../utils-data';

export interface TableField {
  ref: string;
  name: string;
  level: number;
  classifier: string;
  collapsible?: boolean;
}

interface PaginationData {
  fields?: Record<string, unknown>;
  periods?: unknown[];
}

interface PaginationParams {
  data: PaginationData[];
  fields: TableField[];
  allFinFieldsArray: DataField[];
  collapsedFields: string[];
  headerHeight: number;
  rowHeight: number;
  rowHeightByType: Record<string, number>;
  initialSpace: number;
  maxSpace: number;
  breakSpace: number;
  titlesSize: number;
  columnsNumber: number;
  maxColumnsPerTable: number;
  zoom: number;
  doNotBreak?: boolean;
}

interface PaginationResult {
  fieldsByPage: TableField[][];
  topMargins: number[];
  topPadding: number;
}

/**
 * Filters fields to only those that have data (or have children with data),
 * then applies collapsed-field filtering.
 */
export const getAvailableFields = (
  fields: TableField[],
  data: PaginationData[],
  allFinFieldsArray: DataField[],
  collapsedFields: string[]
): TableField[] => {
  const allKeys = data.reduce((acc, cur) => [...acc, ...Object.keys(cur?.fields || {})], [] as string[]);

  // Include accounts not returned with data but that have children with data
  const allAccounts = allFinFieldsArray.filter(f => f.classifier === 'account');
  allAccounts.forEach(acc => {
    if (!allKeys.includes(acc.ref) && acc.children?.find(f => allKeys.includes(f.ref))) {
      allKeys.push(acc.ref);
    }
  });

  let availableFields = fields.filter(f => allKeys.includes(f.ref));
  availableFields = availableFields.map((f, i) => ({
    ...f,
    collapsible: i < availableFields.length - 1 && f.level < availableFields[i + 1].level
  }));

  const filteredFields: TableField[] = [];
  let excludeNext = false;
  let level = 0;
  availableFields.forEach(f => {
    if (f.level <= level) {
      excludeNext = false;
    }
    if (!excludeNext) {
      filteredFields.push(f);
      if (f.collapsible && collapsedFields.includes(`${f.ref}#level-${f.level}`)) {
        excludeNext = true;
        level = f.level;
      }
    }
  });

  return filteredFields;
};

/**
 * Builds the array of row heights for a complete table (including horizontal pagination repeats).
 * Also builds the matching tableFields array with header markers.
 */
const buildRowHeightsAndFields = (
  filteredFields: TableField[],
  headerHeight: number,
  rowHeight: number,
  rowHeightByType: Record<string, number>,
  columnsNumber: number,
  maxColumnsPerTable: number,
  zoom: number
): { rowHeights: number[]; tableFields: TableField[] } => {
  const rowsHeightsForOneTable: number[] = filteredFields.map(
    f => rowHeightByType[mapClassifierToTableRowOption(f.classifier, f.ref)] || rowHeight
  );

  const rowHeights = [headerHeight, ...rowsHeightsForOneTable];
  const tableFields: TableField[] = [{ ref: 'header', name: 'header', level: 0, classifier: '' }, ...filteredFields];

  const tableIsPaginated = maxColumnsPerTable && maxColumnsPerTable < columnsNumber;
  if (tableIsPaginated) {
    const repeatedTablesNumber = Math.ceil((columnsNumber - maxColumnsPerTable) / maxColumnsPerTable);
    for (let i = 0; i < repeatedTablesNumber; i++) {
      rowHeights.push(headerHeight + 8 * zoom, ...rowsHeightsForOneTable);
      tableFields.push({ ref: 'header', name: 'header', level: 0, classifier: '' }, ...filteredFields);
    }
  }

  return { rowHeights, tableFields };
};

/**
 * Calculates how fields should be distributed across pages, and the margins for each page.
 */
export const calculatePagination = ({
  data,
  fields,
  allFinFieldsArray,
  collapsedFields,
  headerHeight,
  rowHeight,
  rowHeightByType,
  initialSpace,
  maxSpace,
  breakSpace,
  titlesSize,
  columnsNumber,
  maxColumnsPerTable,
  zoom,
  doNotBreak
}: PaginationParams): PaginationResult => {
  const hasData = data?.length && data[0] && data[0]?.periods?.length;
  if (!hasData) {
    return { fieldsByPage: [], topMargins: [], topPadding: 0 };
  }

  const filteredFields = getAvailableFields(fields, data, allFinFieldsArray, collapsedFields);

  if (!filteredFields.length) {
    return { fieldsByPage: [], topMargins: [], topPadding: 0 };
  }

  if (doNotBreak) {
    const tableFields: TableField[] = [{ ref: 'header', name: 'header', level: 0, classifier: '' }, ...filteredFields];
    if (maxColumnsPerTable && maxColumnsPerTable < columnsNumber) {
      const repeatedTablesNumber = Math.ceil((columnsNumber - maxColumnsPerTable) / maxColumnsPerTable);
      for (let i = 0; i < repeatedTablesNumber; i++) {
        tableFields.push({ ref: 'header', name: 'header', level: 0, classifier: '' }, ...filteredFields);
      }
    }
    return { fieldsByPage: [tableFields], topMargins: [0], topPadding: 0 };
  }

  const { rowHeights, tableFields } = buildRowHeightsAndFields(
    filteredFields,
    headerHeight,
    rowHeight,
    rowHeightByType,
    columnsNumber,
    maxColumnsPerTable,
    zoom
  );

  let addedRows = 0;
  const newFields: TableField[][] = [];
  const tMargins: number[] = [];
  let topPadding = 0;

  // Check if space is enough to display table titles, header and at least one row
  if (initialSpace < titlesSize + rowHeights[0] + rowHeights[1]) {
    topPadding = initialSpace + breakSpace;
  } else {
    const [n, takenSpace] = getRowsPerAvailableSpace(initialSpace - titlesSize, rowHeights, 0);
    addedRows = n;
    newFields.push(tableFields.slice(0, addedRows));
    tMargins.push(initialSpace - titlesSize - takenSpace);
  }

  const availableSpace = maxSpace - titlesSize - headerHeight;
  while (addedRows < tableFields.length) {
    // If page starts with a header, remove the inter-table margin from its height
    if (tableFields[addedRows].ref === 'header') {
      rowHeights[addedRows] -= 8 * zoom;
    }
    const [n, takenSpace] = getRowsPerAvailableSpace(availableSpace, rowHeights, addedRows);
    newFields.push(tableFields.slice(addedRows, addedRows + n));
    tMargins.push(availableSpace - takenSpace);
    addedRows += n;
  }

  return { fieldsByPage: newFields, topMargins: tMargins, topPadding };
};
